import React, { useMemo, useState } from 'react';
import applicationService from '../services/applicationService';

const STEP_ORDER = [
  { key: 'agreement', label: 'Agreement' },
  { key: 'payment', label: 'Payment' },
  { key: 'verification', label: 'Verification Status' },
  { key: 'esign', label: 'E-Sign' },
  { key: 'completed', label: 'Completed' }
];

const STATUS_STEPS = {
  agreement_pending: 1,
  agreement_accepted: 1,
  payment_pending: 2,
  payment_uploaded: 3,
  payment_verified: 3,
  esign_pending: 4,
  esign_completed: 4,
  completed: 5
};

const badgeClass = (value) => {
  if (value === 'verified') return 'bg-[#d4edda] text-[#155724]';
  if (value === 'rejected') return 'bg-[#f8d7da] text-[#721c24]';
  return 'bg-[#fff3cd] text-[#856404]';
};

const refundBadgeClass = (value) => {
  if (value === 'approved') return 'bg-[#d4edda] text-[#155724]';
  if (value === 'rejected') return 'bg-[#f8d7da] text-[#721c24]';
  return 'bg-[#fff3cd] text-[#856404]';
};

const DEFAULT_BANK_DETAILS = {
  bankName: 'HDFC Bank',
  accountName: 'Jupiter International',
  accountNumber: '50200040099562',
  ifscCode: 'HDFC0002043',
  branchName: 'Chennai CIT nagar'
};

const AgreementWorkflowSection = ({ application, userRole, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [transactionId, setTransactionId] = useState('');
  const [paymentFile, setPaymentFile] = useState(null);
  const [esignProofFile, setEsignProofFile] = useState(null);

  const [reviewReason, setReviewReason] = useState('');
  const [esignLink, setEsignLink] = useState(application?.agreementWorkflow?.esign?.esignLink || '');
  const [refundReason, setRefundReason] = useState('');

  const isCandidate = userRole === 'CANDIDATE';
  const isReviewer = ['HR', 'ADMIN', 'CHAIRMAN'].includes(userRole);

  const paymentStatus = application?.agreementWorkflow?.payment?.paymentStatus || 'pending';
  const refundRequest = application?.agreementWorkflow?.refundRequest;
  const appStatus = application?.applicationStatus || 'agreement_pending';
  const unlockedStep = STATUS_STEPS[appStatus] || 1;

  const canAcceptAgreement = isCandidate && !!application?.agreementWorkflow?.agreementFile && !application?.agreementWorkflow?.isAccepted;
  const canSubmitPayment = isCandidate && application?.agreementWorkflow?.isAccepted && paymentStatus !== 'verified' && appStatus !== 'payment_uploaded';
  const canReviewPayment = isReviewer && appStatus === 'payment_uploaded';
  const canRequestRefund =
    isCandidate &&
    paymentStatus === 'verified' &&
    (!refundRequest?.status || refundRequest.status === 'rejected');
  const canSetEsignLink = isReviewer && paymentStatus === 'verified';
  const canMarkEsignComplete = isCandidate && !!application?.agreementWorkflow?.esign?.esignLink && !application?.agreementWorkflow?.esign?.verifiedAt;
  const canUploadEsignProof = isReviewer && application?.agreementWorkflow?.esign?.isSigned && !application?.agreementWorkflow?.esign?.completionProofDoc;
  const canReviewEsign = isReviewer && application?.agreementWorkflow?.esign?.isSigned && !!application?.agreementWorkflow?.esign?.completionProofDoc && !application?.agreementWorkflow?.esign?.verifiedAt;

  const esignStatus = application?.agreementWorkflow?.esign?.verifiedAt
    ? 'verified'
    : application?.agreementWorkflow?.esign?.rejectedReason
      ? 'rejected'
      : application?.agreementWorkflow?.esign?.isSigned
        ? 'pending'
        : 'pending';

  const bankDetails = useMemo(() => {
    const snapshot = application?.agreementWorkflow?.bankDetailsSnapshot || {};
    const merged = {
      ...DEFAULT_BANK_DETAILS,
      ...snapshot
    };

    return [
      ['Bank Name', merged.bankName],
      ['Account Name', merged.accountName],
      ['Account Number', merged.accountNumber],
      ['IFSC Code', merged.ifscCode],
      ['Branch', merged.branchName]
    ].filter((row) => row[1]);
  }, [application]);

  const validateUpload = (file) => {
    if (!file) return 'File is required';
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) return 'Only JPG, PNG, and PDF are allowed';
    if (file.size > 5 * 1024 * 1024) return 'File must be less than 5MB';
    return '';
  };

  const withAction = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await onRefresh();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <h3 className="m-0 mb-4 text-base font-semibold text-primary pb-1 border-b border-accent-green">Agreement Workflow</h3>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {STEP_ORDER.map((step, idx) => {
          const number = idx + 1;
          const active = number <= unlockedStep;
          return (
            <div
              key={step.key}
              className={`rounded-lg border p-2 text-center ${active ? 'border-accent-green bg-[#f6faf2]' : 'border-gray-200 bg-gray-50 opacity-70'}`}
            >
              <div className={`text-xs font-bold ${active ? 'text-accent-green' : 'text-gray-400'}`}>Step {number}</div>
              <div className="text-xs text-primary mt-1">{step.label}</div>
            </div>
          );
        })}
      </div>

      {error && <div className="bg-[#f8d7da] text-[#721c24] p-2 rounded mb-3 text-sm">{error}</div>}

      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="font-semibold text-sm mb-2">1. Agreement</div>
          {application?.agreementWorkflow?.agreementFile ? (
            <a className="text-sm text-[#007bff]" href={application.agreementWorkflow.agreementFile} target="_blank" rel="noreferrer">View Agreement Document</a>
          ) : (
            <div className="text-sm text-gray-500">Country agreement is not configured yet.</div>
          )}
          {application?.agreementWorkflow?.agreementSourceCountry && (
            <div className="text-sm mt-2">Agreement Country: <span className="font-semibold">{application.agreementWorkflow.agreementSourceCountry}</span></div>
          )}
          <div className="text-sm mt-2">Amount: <span className="font-semibold">{application?.agreementWorkflow?.amount ?? 0}</span></div>

          {isReviewer && (
            <div className="text-xs text-gray-500 mt-2">
              Agreement is managed in Job Management by country settings.
            </div>
          )}

          {canAcceptAgreement && (
            <button
              disabled={busy}
              className="mt-3 py-2 px-3 rounded bg-accent-green text-white disabled:opacity-60"
              onClick={() => withAction(() => applicationService.acceptAgreement(application._id))}
            >Accept Terms and Conditions</button>
          )}

          {isCandidate && !application?.agreementWorkflow?.agreementFile && (
            <div className="mt-2 text-sm text-[#a94442]">Agreement is not available for your destination country yet. Please contact admin.</div>
          )}

          {application?.agreementWorkflow?.isAccepted && (
            <div className="mt-2 text-sm text-[#155724]">Agreement accepted</div>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg p-3">
          <div className="font-semibold text-sm mb-2">2. Payment</div>
          {isCandidate && (
            <div className="text-sm mb-2 text-[#155724]">Use the below account details to pay at bank.</div>
          )}
          {bankDetails.length > 0 && (
            <div className="text-sm mb-2">
              {bankDetails.map(([label, value]) => (
                <div key={label}><span className="font-semibold">{label}:</span> {value}</div>
              ))}
            </div>
          )}
          <div className="text-sm mb-2">Payment status: <span className={`px-2 py-1 rounded text-xs ${badgeClass(paymentStatus)}`}>{paymentStatus}</span></div>
          {application?.agreementWorkflow?.payment?.transactionId && (
            <div className="text-sm mb-1">Transaction ID: <span className="font-semibold">{application.agreementWorkflow.payment.transactionId}</span></div>
          )}
          {application?.agreementWorkflow?.payment?.transactionDoc && (
            <div className="text-sm mb-2">
              Transaction Document:{' '}
              <a
                className="text-[#007bff]"
                href={application.agreementWorkflow.payment.transactionDoc}
                target="_blank"
                rel="noreferrer"
              >
                View Uploaded Proof
              </a>
            </div>
          )}
          {application?.agreementWorkflow?.payment?.verifiedAt && (
            <div className="text-sm text-[#155724] mb-2">Verified at: {new Date(application.agreementWorkflow.payment.verifiedAt).toLocaleString()}</div>
          )}
          {application?.agreementWorkflow?.payment?.rejectedReason && (
            <div className="text-sm text-[#a94442] mb-2">Reason: {application.agreementWorkflow.payment.rejectedReason}</div>
          )}

          {refundRequest?.status && (
            <div className="mt-2 mb-2 text-sm">
              Refund request status:{' '}
              <span className={`px-2 py-1 rounded text-xs ${refundBadgeClass(refundRequest.status)}`}>
                {refundRequest.status}
              </span>
            </div>
          )}

          {refundRequest?.reason && (
            <div className="text-sm mb-1">Refund reason: <span className="font-semibold">{refundRequest.reason}</span></div>
          )}

          {refundRequest?.reviewDecisionReason && (
            <div className="text-sm text-[#a94442] mb-2">Chairman note: {refundRequest.reviewDecisionReason}</div>
          )}

          {canSubmitPayment && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input type="text" placeholder="Transaction ID" value={transactionId} onChange={(e) => setTransactionId(e.target.value)} className="p-2 border rounded" />
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPaymentFile(e.target.files?.[0])} className="p-2 border rounded" />
              <button
                disabled={busy}
                className="py-2 px-3 rounded bg-accent-green text-white disabled:opacity-60"
                onClick={() => withAction(async () => {
                  if (!transactionId.trim()) throw new Error('Transaction ID is required');
                  const fileError = validateUpload(paymentFile);
                  if (fileError) throw new Error(fileError);
                  await applicationService.uploadPayment(application._id, {
                    transactionId,
                    file: paymentFile
                  });
                })}
              >Submit Payment</button>
            </div>
          )}

          {canReviewPayment && (
            <div className="mt-2">
              <input type="text" placeholder="Reason for reject (only if rejecting)" value={reviewReason} onChange={(e) => setReviewReason(e.target.value)} className="p-2 border rounded w-full mb-2" />
              <div className="flex gap-2">
                <button disabled={busy} className="py-2 px-3 rounded bg-[#28a745] text-white disabled:opacity-60" onClick={() => withAction(() => applicationService.reviewPayment(application._id, 'verify'))}>Verify Payment</button>
                <button disabled={busy} className="py-2 px-3 rounded bg-[#dc3545] text-white disabled:opacity-60" onClick={() => withAction(async () => {
                  if (!reviewReason.trim()) throw new Error('Reason is required');
                  await applicationService.reviewPayment(application._id, 'reject', reviewReason);
                })}>Reject Payment</button>
              </div>
            </div>
          )}

          {canRequestRefund && (
            <div className="mt-3 border border-gray-200 rounded p-3 bg-[#fffaf0]">
              <div className="text-sm font-semibold mb-2">Request Payment Refund</div>
              <input
                type="text"
                placeholder="Reason for refund (minimum 5 characters)"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="p-2 border rounded w-full mb-2"
              />
              <button
                disabled={busy}
                className="py-2 px-3 rounded bg-[#d9534f] text-white disabled:opacity-60"
                onClick={() => withAction(async () => {
                  if (refundReason.trim().length < 5) throw new Error('Please enter a valid refund reason');
                  await applicationService.requestPaymentRefund(application._id, refundReason.trim());
                  setRefundReason('');
                })}
              >
                Submit Refund Request
              </button>
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg p-3">
          <div className="font-semibold text-sm mb-2">3. Verification Status</div>
          <div className="text-sm">Agreement accepted: {application?.agreementWorkflow?.isAccepted ? 'Yes' : 'No'}</div>
          <div className="text-sm">Payment verified: {paymentStatus === 'verified' ? 'Yes' : 'No'}</div>
          <div className="text-sm">E-sign verified: {application?.agreementWorkflow?.esign?.verifiedAt ? 'Yes' : 'No'}</div>
        </div>

        <div className="border border-gray-200 rounded-lg p-3">
          <div className="font-semibold text-sm mb-2">4. E-Sign</div>
          <div className="text-sm mb-2">E-sign status: <span className={`px-2 py-1 rounded text-xs ${badgeClass(esignStatus)}`}>{esignStatus}</span></div>
          {application?.agreementWorkflow?.esign?.esignLink ? (
            <a className="text-sm text-[#007bff]" href={application.agreementWorkflow.esign.esignLink} target="_blank" rel="noreferrer">Open E-Sign Link</a>
          ) : (
            <div className="text-sm text-gray-500">E-sign link not added yet.</div>
          )}

          {application?.agreementWorkflow?.esign?.signedAt && (
            <div className="text-sm mt-2">Candidate marked signed at: <span className="font-semibold">{new Date(application.agreementWorkflow.esign.signedAt).toLocaleString()}</span></div>
          )}

          {application?.agreementWorkflow?.esign?.completionProofDoc && (
            <div className="text-sm mt-2">
              E-sign proof document:{' '}
              <a
                className="text-[#007bff]"
                href={application.agreementWorkflow.esign.completionProofDoc}
                target="_blank"
                rel="noreferrer"
              >
                View Uploaded Proof
              </a>
            </div>
          )}

          {application?.agreementWorkflow?.esign?.verifiedAt && (
            <div className="text-sm text-[#155724] mt-1">Verified at: {new Date(application.agreementWorkflow.esign.verifiedAt).toLocaleString()}</div>
          )}

          {application?.agreementWorkflow?.esign?.rejectedReason && (
            <div className="text-sm text-[#a94442] mt-1">Reason: {application.agreementWorkflow.esign.rejectedReason}</div>
          )}

          {canSetEsignLink && (
            <div className="mt-2 flex gap-2 md:flex-row flex-col">
              <a
                href="https://app1.leegality.com/login"
                target="_blank"
                rel="noreferrer"
                className="py-2 px-3 rounded bg-[#1b355b] text-white text-center"
              >
                Open Leegality Login
              </a>
              <input type="url" placeholder="https://esign-link" value={esignLink} onChange={(e) => setEsignLink(e.target.value)} className="p-2 border rounded flex-1" />
              <button disabled={busy} className="py-2 px-3 rounded bg-accent-green text-white disabled:opacity-60" onClick={() => withAction(async () => {
                if (!esignLink.trim()) throw new Error('E-sign link is required');
                await applicationService.addEsignLink(application._id, esignLink.trim());
              })}>Save Link</button>
            </div>
          )}

          {canMarkEsignComplete && (
            <div className="mt-2">
              <button
                disabled={busy}
                className="py-2 px-3 rounded bg-accent-green text-white disabled:opacity-60"
                onClick={() => withAction(() => applicationService.markEsignComplete(application._id))}
              >
                Mark E-Sign Viewed
              </button>
            </div>
          )}

          {canUploadEsignProof && (
            <div className="mt-2">
              <div className="text-sm text-[#856404] mb-2">Candidate completed e-sign. Upload proof document to continue.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setEsignProofFile(e.target.files?.[0] || null)}
                  className="p-2 border rounded"
                />
                <button
                  disabled={busy}
                  className="py-2 px-3 rounded bg-accent-green text-white disabled:opacity-60"
                  onClick={() => withAction(async () => {
                    const fileError = validateUpload(esignProofFile);
                    if (fileError) throw new Error(fileError);
                    await applicationService.uploadDocument(application._id, esignProofFile, 'ESIGN_PROOF');
                    setEsignProofFile(null);
                  })}
                >
                  Upload E-Sign Proof
                </button>
              </div>
            </div>
          )}

          {canReviewEsign && (
            <div className="mt-2">
              <input type="text" placeholder="Reason for reject (only if rejecting)" value={reviewReason} onChange={(e) => setReviewReason(e.target.value)} className="p-2 border rounded w-full mb-2" />
              <div className="flex gap-2">
                <button disabled={busy} className="py-2 px-3 rounded bg-[#28a745] text-white disabled:opacity-60" onClick={() => withAction(() => applicationService.reviewEsign(application._id, 'verify'))}>Verify E-Sign</button>
                <button disabled={busy} className="py-2 px-3 rounded bg-[#dc3545] text-white disabled:opacity-60" onClick={() => withAction(async () => {
                  if (!reviewReason.trim()) throw new Error('Reason is required');
                  await applicationService.reviewEsign(application._id, 'reject', reviewReason);
                })}>Reject E-Sign</button>
              </div>
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg p-3">
          <div className="font-semibold text-sm mb-2">5. Completed</div>
          <div className="text-sm">
            {application?.applicationStatus === 'completed'
              ? 'All checks are complete. HR/Admin can now move to next section.'
              : 'Final completion pending required verifications.'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementWorkflowSection;

